import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Sparkles, Lock, Zap, Loader2, ChevronDown, ChevronUp, RefreshCw, ClipboardCopy, MessageSquare,
} from "lucide-react";

const STAR_LABELS = [
  { key: "situation", label: "S — Situation" },
  { key: "task", label: "T — Task" },
  { key: "action", label: "A — Action" },
  { key: "result", label: "R — Result" },
];

export default function InterviewPrepPanel({ applicationId, status }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.tier === "pro";
  const [prep, setPrep] = useState({ generated_at: null, questions: [] });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState({}); // idx -> bool

  const load = async () => {
    if (!applicationId || !isPro) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/applications/${applicationId}/interview-prep`);
      setPrep(data || { generated_at: null, questions: [] });
      // auto-open the first STAR if we have questions
      if (data?.questions?.length) setOpen({ 0: true });
    } catch (err) {
      if (err.response?.status !== 402) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [applicationId, isPro]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/interview-prep", { application_id: applicationId });
      setPrep(data);
      setOpen({ 0: true });
      toast.success("Interview prep generated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const copyStar = (q) => {
    const text = [
      `Q: ${q.q}`,
      "",
      `S: ${q.star.situation}`,
      `T: ${q.star.task}`,
      `A: ${q.star.action}`,
      `R: ${q.star.result}`,
    ].join("\n");
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard.");
  };

  if (!applicationId) {
    return (
      <div
        className="rounded-xl border border-dashed border-[#D4CFC5] bg-[#F4F1EE]/50 p-4 text-sm text-[#5C6B64]"
        data-testid="prep-empty-app"
      >
        Save this application first to generate interview prep.
      </div>
    );
  }

  if (!isPro) {
    return (
      <div
        className="rounded-xl bg-[#1A362D] text-[#F4F1EE] p-4 flex items-start gap-3"
        data-testid="prep-paywall"
      >
        <Lock className="w-4 h-4 text-[#D99C3D] shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium">Prep for this interview with AI</p>
          <p className="text-sm opacity-80 mt-0.5">
            Claude generates 5 likely questions with STAR starter answers tailored to the role — a Trackr Pro feature.
          </p>
        </div>
        <Button
          onClick={() => navigate("/upgrade")}
          className="rounded-full bg-[#D99C3D] hover:bg-[#C48A2E] text-[#1A362D] font-semibold shrink-0"
          data-testid="prep-upgrade-btn"
        >
          <Zap className="w-3.5 h-3.5 mr-1" /> Upgrade
        </Button>
      </div>
    );
  }

  const hasPrep = (prep.questions || []).length > 0;

  return (
    <div className="space-y-3" data-testid="prep-panel">
      {!hasPrep && !loading && (
        <div
          className="rounded-xl bg-[#FDF2DF] border border-[#D99C3D]/30 p-5"
          data-testid="prep-empty"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1A362D] text-[#D99C3D] flex items-center justify-center shrink-0">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-[#1A362D]">Ready to prep for this interview?</p>
              <p className="text-sm text-[#5C6B64] mt-0.5">
                Claude will draft 5 likely questions and a STAR starter answer for each, drawn from the role and your background.
              </p>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white shrink-0"
              data-testid="prep-generate-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Thinking…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-1.5" /> Generate prep</>
              )}
            </Button>
          </div>
        </div>
      )}

      {hasPrep && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#5C6B64]" data-testid="prep-generated-at">
              {prep.generated_at
                ? `Generated ${new Date(prep.generated_at).toLocaleString()}`
                : null}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              disabled={generating}
              className="rounded-full"
              data-testid="prep-regenerate-btn"
            >
              {generating ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Regenerating…</>
              ) : (
                <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate</>
              )}
            </Button>
          </div>

          <div className="space-y-2">
            {prep.questions.map((q, i) => {
              const isOpen = !!open[i];
              return (
                <div
                  key={i}
                  className="rounded-xl bg-white border border-[#F0EDE7] overflow-hidden"
                  data-testid={`prep-q-${i}`}
                >
                  <button
                    onClick={() => setOpen((s) => ({ ...s, [i]: !s[i] }))}
                    className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#F4F1EE] transition-colors"
                    data-testid={`prep-q-toggle-${i}`}
                  >
                    <span className="shrink-0 w-6 h-6 rounded-full bg-[#1A362D] text-[#D99C3D] text-xs flex items-center justify-center font-semibold mt-0.5">
                      {i + 1}
                    </span>
                    <span className="flex-1 text-sm text-[#1A362D] font-medium">{q.q}</span>
                    {isOpen ? (
                      <ChevronUp className="w-4 h-4 text-[#5C6B64] shrink-0 mt-1" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#5C6B64] shrink-0 mt-1" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 space-y-3 border-t border-[#F0EDE7]">
                      {q.why && (
                        <p className="text-xs italic text-[#5C6B64]" data-testid={`prep-why-${i}`}>
                          Why: {q.why}
                        </p>
                      )}
                      <div className="space-y-2">
                        {STAR_LABELS.map(({ key, label }) => (
                          <div key={key} data-testid={`prep-star-${key}-${i}`}>
                            <p className="text-xs font-semibold text-[#D99C3D] tracking-wider uppercase">{label}</p>
                            <p className="text-sm text-[#1A362D] mt-0.5 leading-relaxed">
                              {q.star?.[key] || <span className="text-[#5C6B64]">—</span>}
                            </p>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => copyStar(q)}
                        className="inline-flex items-center gap-1.5 text-xs text-[#5C6B64] hover:text-[#1A362D] underline"
                        data-testid={`prep-copy-${i}`}
                      >
                        <ClipboardCopy className="w-3 h-3" /> Copy question + STAR
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
